import React, { useState, useEffect, useRef, useCallback } from 'react';
import useStore from '../../../../store';
import { useTranslation } from 'react-i18next';
import { FaCheck, FaTimes, FaExclamationTriangle } from 'react-icons/fa';
import UnifiedLeaderboard from '../../ui/UnifiedLeaderboard';
import './WordDuel.css';
import '../../ui/ArcadeShared.css';

const WordDuel = ({ socket, onClose, timeLeft: totalTimeLeft }) => {
  const { t } = useTranslation();
  const store = useStore.getState();

  const [state, setState] = useState(null);
  const [inputVal, setInputVal] = useState('');
  const [localTimer, setLocalTimer] = useState(100);
  const [finalData, setFinalData] = useState(null);

  // Основной массив истории, который хранит и серверные ходы, и локальные ошибки
  const [displayHistory, setDisplayHistory] = useState([]);
  const [isBotThinking, setIsBotThinking] = useState(false);

  const inputValRef = useRef(''); // Позволяет сокету видеть текст без перезагрузки useEffect

  const historyRef = useRef(null);
  const inputRef = useRef(null); // Ссылка на поле ввода
  const timerInterval = useRef(null);
  const lastTaskTime = useRef(Date.now());

  // Синхронизируем реф с состоянием инпута
  useEffect(() => {
    inputValRef.current = inputVal;
  }, [inputVal]);

  // Универсальная функция возврата фокуса
  const returnFocus = useCallback(() => {
    if (inputRef.current && !finalData) {
      inputRef.current.focus();
    }
  }, [finalData]);

  // Функция ПОЛНОЙ очистки поля (как в Словоделе)
  const handleClearAll = (e) => {
    e?.preventDefault();
    setInputVal('');
    window.croco?.haptic?.('selection');
    returnFocus();
  };

  // Авто-скролл истории
  useEffect(() => {
    if (historyRef.current) {
      historyRef.current.scrollTop = historyRef.current.scrollHeight;
    }
  }, [displayHistory, isBotThinking]);

  // Следим за фокусом: при монтировании и при любых изменениях состояния
  useEffect(() => {
    returnFocus();
  }, [returnFocus, state, isBotThinking]);

  useEffect(() => {
    if (!socket) return;
    store.ref.work.methods.hostAction('mafiaMiniGameAction', { type: 'init' });

    const handleEvent = (data) => {
      if (data.type === 'update') {
        setState(data.state);
        setDisplayHistory(data.state.history);
      }
      else if (data.type === 'next') {
        const newState = data.state;

        // Логика успеха:
        // 1. Находим в локальной истории наше "pending" слово и удаляем его (заменим на официальные данные)
        // 2. Берем два последних слова из серверной истории (игрок + ответ бота)
        // 3. Добавляем их в конец локальной истории
        const officialMoves = newState.history.slice(-2);

        setDisplayHistory(prev => {
          const base = prev.filter(item => item.status !== 'pending');
          return [...base, ...officialMoves];
        });

        setIsBotThinking(false);
        setState(newState);
        lastTaskTime.current = Date.now();
        window.croco?.haptic?.('success');
      }
      else if (data.type === 'reject') {
        // Логика ошибки:
        setIsBotThinking(false);
        window.croco?.haptic?.('error');

        const errorText = t(`arcade.wordduel_err_${data.reason}`, data.reason.toUpperCase());

        // Превращаем временное слово в постоянную запись об ошибке
        setDisplayHistory(prev => {
          return prev.map(item => {
            if (item.status === 'pending') {
              return {
                ...item,
                status: 'rejected',
                errorText: errorText
              };
            }
            return item;
          });
        });

        returnFocus();
      }
      else if (data.type === 'game_over') {
        setFinalData(data);
      }
    };

    socket.on('arcade:event', handleEvent);
    // В зависимости больше НЕ кладем inputVal, чтобы не "моргал" сокет
    return () => socket.off('arcade:event', handleEvent);
  }, [socket, t, returnFocus, store.ref.work.methods]);

  // Таймер хода
  useEffect(() => {
    if (!state || state.completed || state.isFirstMove || isBotThinking) return;

    timerInterval.current = setInterval(() => {
      const elapsed = Date.now() - lastTaskTime.current;
      const pct = Math.max(0, 100 - (elapsed / state.duration) * 100);
      setLocalTimer(pct);

      if (pct <= 0) {
        clearInterval(timerInterval.current);
        store.ref.work.methods.hostAction('mafiaMiniGameAction', { type: 'timeout' });
      }
    }, 100);

    return () => clearInterval(timerInterval.current);
  }, [state, isBotThinking, store.ref.work.methods]);

  const handleSubmit = (e) => {
    e.preventDefault();

    // Если бот "думает", мы просто игнорируем отправку,
    // но инпут при этом остается активным и фокус не теряется
    if (isBotThinking || !inputVal.trim()) return;

    const word = inputVal.trim();


    // --- МОМЕНТАЛЬНАЯ РЕАКЦИЯ ---
    // 1. Добавляем слово в историю как "ожидающее"
    const pendingItem = {
      who: 'player',
      word: word,
      status: 'pending'
    };
    setDisplayHistory(prev => [...prev, pendingItem]);

    // 2. Сразу показываем, что бот начал думать
    setIsBotThinking(true);
    setInputVal('');

    // 3. Отправляем на сервер
    store.ref.work.methods.hostAction('mafiaMiniGameAction', {
      type: 'submit',
      word: word
    });
  };

  const formatWord = (word) => {
    if (!word) return '';
    const lastLetter = word.slice(-1);
    const isSpecial = ['ь', 'ъ', 'ы'].includes(lastLetter.toLowerCase());

    if (isSpecial) {
      const body = word.slice(0, -2);
      const target = word.slice(-2, -1);
      const tail = word.slice(-1);
      return <>{body}<span className="wd-letter-hint">{target.toUpperCase()}</span>{tail}</>;
    } else {
      const body = word.slice(0, -1);
      const target = word.slice(-1);
      return <>{body}<span className="wd-letter-hint">{target.toUpperCase()}</span></>;
    }
  };

  // Хелпер для обрезки текста по длине (100 символов)
  const truncateDefinition = (text) => {
    if (!text) return null;
    return text.length > 100 ? text.substring(0, 100) + '...' : text;
  };

  if (finalData) {
    const { score, rank } = finalData.my;
    return (
      <div className="mafia-overlay-bg">
        <div className="cs-report-card">
          <div className="cs-report-header">
            <div className="cs-report-title">{t('arcade.wordduel_result', 'РЕЗУЛЬТАТ ДОПРОСА')}</div>
          </div>

          <div className="cs-stat-row">
             <span className="cs-stat-label">{t('arcade.wordduel_score', 'ОЧКИ')}</span>
             <span className="cs-stat-value">{score} ⭐️</span>
          </div>
          <div className="cs-stat-row">
             <span className="cs-stat-label">{t('arcade.wordduel_rank', 'ЗВАНИЕ')}</span>
             <span className="cs-stat-value rank-text">{t(`arcade.wordduel_rank_${rank}`, rank)}</span>
          </div>

          <UnifiedLeaderboard
              results={finalData.leaderboard}
              myId={store.ref.telegram.telegramUserId}
              t={t}
          />

          <button className="cs-close-btn" style={{marginTop: 'auto'}} onClick={onClose}>{t('arcade.close', 'ПОДШИТЬ В ДЕЛО')}</button>
        </div>
      </div>
    );
  }

  if (!state) return null;

  return (
    <div
      className="wordduel-container"
      onClick={returnFocus} // Клик в любое место экрана вернет фокус в инпут
    >
      <div className="sd-header">
        <div className="sd-title">{t('arcade.wordduel_title')}</div>
        <div className="sd-score">{state.score} ⭐️</div>
      </div>

      <div className="wd-history add-scroll touch-active" ref={historyRef}>
        {displayHistory.map((item, i) => {
          // --- РЕНДЕР ОШИБКИ (НОВАЯ ЛОГИКА) ---
          if (item.status === 'rejected') {
            return (
              <div key={i} className="wd-rejected-row">
                <div className="wd-error-badge">
                  <FaExclamationTriangle style={{marginRight: '6px'}} />
                  {item.errorText}
                </div>
                <div className="wd-bubble player rejected">
                  <div className="wd-word">{item.word}</div>
                </div>
              </div>
            );
          }

          // Обычный пузырек (Бот или Игрок)
          return (
            <div key={i} className={`wd-bubble ${item.who} ${item.status === 'pending' ? 'pending' : ''}`}>
              <div className="wd-word">{formatWord(item.word)}</div>
              {item.who === 'bot' && item.definition && (
                <div className="wd-definition">{truncateDefinition(item.definition)}</div>
              )}
            </div>
          );
        })}

        {/* Индикатор печати бота */}
        {isBotThinking && (
          <div className="wd-typing-dots">
            <div className="wd-dot"></div><div className="wd-dot"></div><div className="wd-dot"></div>
          </div>
        )}

        {displayHistory.length === 0 && (
          <div style={{textAlign: 'center', opacity: 0.5, marginTop: '20px'}}>
            {t('arcade.wordduel_start_hint', 'Введите любое существительное, чтобы начать цепь')}
          </div>
        )}
      </div>

      <div className="wd-next-letter-banner">
        {state.isFirstMove ? (
          t('arcade.wordduel_first_move', 'ОЖИДАНИЕ ПЕРВОГО СЛОВА...')
        ) : (
          <>
            {t('arcade.wordduel_need_letter', 'НУЖНО СЛОВО НА БУКВУ:')} <span className="wd-target">{state.nextLetter?.toUpperCase()}</span>
          </>
        )}
      </div>

      {/* ВОЗВРАЩЕННЫЙ ДИЗАЙН: ИНПУТ С КРЕСТИКОМ + КНОПКА ОТПРАВКИ */}
      <form onSubmit={handleSubmit} className="wd-input-area">
        <div className="wd-input-wrapper">
            <input
              ref={inputRef}
              className={`wd-input ${isBotThinking ? 'locked' : ''}`}
              value={inputVal}
              onChange={(e) => setInputVal(e.target.value)}
              autoFocus
              placeholder="..."
              autoComplete="off"
              autoCorrect="off"
              spellCheck="false"
            />
            {/* Крестик внутри поля */}
            {inputVal && (
                <div className="wd-input-clear-x" onClick={handleClearAll}>
                    <FaTimes />
                </div>
            )}
        </div>

        <button
            type="submit"
            className="wd-submit-btn-square"
            style={{ opacity: isBotThinking ? 0.5 : 1 }}
        >
           <FaCheck />
        </button>
      </form>

      {!state.isFirstMove && !isBotThinking && (
        <div className={`wd-timer-bar ${localTimer < 30 ? 'danger' : ''}`} style={{width: `${localTimer}%`}} />
      )}
    </div>
  );
};

export default WordDuel;